import React, { useState, useEffect, useCallback } from 'react';
import { toUrl } from '../lib/config';

interface GorusmeRow {
  id: number;
  adiSoyadi: string;
  tcKimlikNo: string;
  tedaviGorduguBirim: string;
  protokolNo: string;
  gorusmeTarihi: string;
  gorusmeYapan: string;
  createdAt: string;
}

const SosyalHizmetBirimiYonetim: React.FC = () => {
  const [gorusmeler, setGorusmeler] = useState<GorusmeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGorusme, setSelectedGorusme] = useState<any>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const fetchGorusmeler = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const url = toUrl('/api/sosyal-hizmet-birimi');
      const res = await fetch(url);
      if (!res.ok) {
        const errorText = await res.text();
        console.error('Error response:', errorText);
        throw new Error(`Görüşmeler yüklenemedi: ${res.status}`);
      }
      const data = await res.json();
      setGorusmeler(data.data || []);
    } catch (e: any) {
      console.error('Fetch error:', e);
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGorusmeler();
  }, [fetchGorusmeler]);

  const openDeleteModal = (id: number) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteId(null);
    setShowDeleteModal(false);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    
    try {
      setLoading(true);
      setError('');
      const res = await fetch(toUrl(`/api/sosyal-hizmet-birimi/${deleteId}`), {
        method: 'DELETE'
      });
      if (!res.ok) throw new Error('Silme işlemi başarısız');
      closeDeleteModal();
      await fetchGorusmeler();
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleShowDetail = async (id: number) => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch(toUrl(`/api/sosyal-hizmet-birimi/${id}`));
      if (!res.ok) throw new Error('Görüşme detayı yüklenemedi');
      const data = await res.json();
      setSelectedGorusme(data.data);
      setShowDetailModal(true);
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const filteredGorusmeler = gorusmeler.filter(gorusme =>
    gorusme.adiSoyadi?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    gorusme.tcKimlikNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    gorusme.tedaviGorduguBirim?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    gorusme.protokolNo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="admin-page" style={{ padding: 20 }}>
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Sosyal Hizmet Birimi Hasta Görüşme Yönetimi</h2>
        <div style={{ fontSize: 14, color: '#6b7280' }}>
          Toplam: {gorusmeler.length} görüşme
        </div>
      </div>

      {error && (
        <div style={{ padding: 12, marginBottom: 16, backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: 6 }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <input
          type="text"
          placeholder="Hasta adı, TC No, birim veya protokol numarasına göre ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 15px',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            fontSize: 14
          }}
        />
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 20 }}>Yükleniyor...</div>}

      {!loading && filteredGorusmeler.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
          Henüz görüşme kaydı bulunmuyor.
        </div>
      )}

      {!loading && filteredGorusmeler.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>ID</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Hasta Adı</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>TC No</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Protokol No</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Birim</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Görüşme Tarihi</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Görüşme Yapan</th>
                <th style={{ padding: 12, textAlign: 'center', fontWeight: 600, fontSize: 13 }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredGorusmeler.map((gorusme, idx) => (
                <tr key={gorusme.id} style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: idx % 2 === 0 ? 'white' : '#f9fafb' }}>
                  <td style={{ padding: 12, fontSize: 13 }}>{gorusme.id}</td>
                  <td style={{ padding: 12, fontSize: 13 }}>{gorusme.adiSoyadi || '-'}</td>
                  <td style={{ padding: 12, fontSize: 13 }}>{gorusme.tcKimlikNo || '-'}</td>
                  <td style={{ padding: 12, fontSize: 13 }}>{gorusme.protokolNo || '-'}</td>
                  <td style={{ padding: 12, fontSize: 13 }}>{gorusme.tedaviGorduguBirim || '-'}</td>
                  <td style={{ padding: 12, fontSize: 13 }}>{formatDate(gorusme.gorusmeTarihi)}</td>
                  <td style={{ padding: 12, fontSize: 13 }}>{gorusme.gorusmeYapan || '-'}</td>
                  <td style={{ padding: 12, textAlign: 'center' }}>
                    <button
                      onClick={() => handleShowDetail(gorusme.id)}
                      style={{
                        padding: '6px 12px',
                        marginRight: 8,
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 12
                      }}
                    >
                      Detay
                    </button>
                    <button
                      onClick={() => openDeleteModal(gorusme.id)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 12
                      }}
                    >
                      Sil
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Silme Onay Modal */}
      {showDeleteModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={closeDeleteModal}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: 8,
              padding: 24,
              maxWidth: 400,
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', color: '#991b1b' }}>Görüşme Kaydını Sil</h3>
            <p style={{ margin: '0 0 24px 0', color: '#374151' }}>
              Bu görüşme kaydını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={closeDeleteModal}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: 4,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: 14
                }}
              >
                İptal
              </button>
              <button
                onClick={confirmDelete}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? 'Siliniyor...' : 'Sil'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detay Modal */}
      {showDetailModal && selectedGorusme && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20
          }}
          onClick={() => setShowDetailModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: 8,
              maxWidth: 900,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              padding: 24
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0 }}>Görüşme Detayı - ID: {selectedGorusme.id}</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer'
                }}
              >
                Kapat
              </button>
            </div>

            {/* Hasta Bilgileri */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid #e5e7eb' }}>Hasta Bilgileri</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <DetailItem label="Adı Soyadı" value={selectedGorusme.adiSoyadi} />
                <DetailItem label="TC Kimlik No" value={selectedGorusme.tcKimlikNo} />
                <DetailItem label="Anne Adı" value={selectedGorusme.anneAdi} />
                <DetailItem label="Baba Adı" value={selectedGorusme.babaAdi} />
                <DetailItem label="Doğum Yeri" value={selectedGorusme.dogumYeri} />
                <DetailItem label="Doğum Tarihi" value={formatDate(selectedGorusme.dogumTarihi)} />
              </div>
            </div>

            {/* Tedavi ve İletişim Bilgileri */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid #e5e7eb' }}>Tedavi ve İletişim Bilgileri</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <DetailItem label="Tedavi Gördüğü Birim" value={selectedGorusme.tedaviGorduguBirim} />
                <DetailItem label="Başvuru Tarihi" value={formatDate(selectedGorusme.basvuruTarihi)} />
                <DetailItem label="Protokol No" value={selectedGorusme.protokolNo} />
                <DetailItem label="Sağlık Güvencesi" value={selectedGorusme.saglikGuvencesi} />
                <DetailItem label="Telefon No" value={selectedGorusme.telefonNo} />
              </div>
              <div style={{ marginTop: 12 }}>
                <DetailItem label="Adres" value={selectedGorusme.adres} />
              </div>
            </div>

            {/* Görüşme Bilgileri */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid #e5e7eb' }}>Görüşme Bilgileri</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <DetailItem label="Görüşmenin Yapıldığı Yer" value={selectedGorusme.gorusmeninYapildigiYer} />
                <DetailItem label="Görüşülen Kişi" value={selectedGorusme.gorusdenKisi} />
                <DetailItem label="Görüşme Tarihi" value={formatDate(selectedGorusme.gorusmeTarihi)} />
                <DetailItem label="Görüşmeyi Yapan" value={selectedGorusme.gorusmeYapan} />
              </div>
            </div>

            {/* Başvuru Nedeni */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid #e5e7eb' }}>Başvuru Nedeni</h4>
              <div style={{ 
                padding: 16, 
                backgroundColor: '#f9fafb', 
                borderRadius: 6, 
                fontSize: 14,
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap'
              }}>
                {selectedGorusme.sosyalHizmetBirimine || '-'}
              </div>
            </div>

            {/* Değerlendirmeler */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid #e5e7eb' }}>Değerlendirmeler</h4>
              
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#374151' }}>Hastanın Sağlık ve Fiziksel Durumu</div>
                <div style={{ 
                  padding: 12, 
                  backgroundColor: '#fef3c7', 
                  borderRadius: 6, 
                  fontSize: 13,
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap'
                }}>
                  {selectedGorusme.hastanınSaglikVeFizikseli || '-'}
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#374151' }}>Hastanın Psiko-Sosyal ve Ailevi Durumu</div>
                <div style={{ 
                  padding: 12, 
                  backgroundColor: '#dbeafe', 
                  borderRadius: 6, 
                  fontSize: 13,
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap'
                }}>
                  {selectedGorusme.hastanınPsiko || '-'}
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#374151' }}>Hastanın Barınma ve Ekonomik Durumu</div>
                <div style={{ 
                  padding: 12, 
                  backgroundColor: '#fce7f3', 
                  borderRadius: 6, 
                  fontSize: 13,
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap'
                }}>
                  {selectedGorusme.hastanınBarinma || '-'}
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#374151' }}>Değerlendirme</div>
                <div style={{ 
                  padding: 12, 
                  backgroundColor: '#e0e7ff', 
                  borderRadius: 6, 
                  fontSize: 13,
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap'
                }}>
                  {selectedGorusme.degerlendirme || '-'}
                </div>
              </div>

              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#374151' }}>Sonuç ve Öneriler</div>
                <div style={{ 
                  padding: 12, 
                  backgroundColor: '#d1fae5', 
                  borderRadius: 6, 
                  fontSize: 13,
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap'
                }}>
                  {selectedGorusme.sonucVeOneriler || '-'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DetailItem: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div style={{ marginBottom: 8 }}>
    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 14, fontWeight: 500 }}>{value || '-'}</div>
  </div>
);

export default SosyalHizmetBirimiYonetim;
